import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    Button,
    TablePagination,
    Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { FDCM } from '../fastDeliveryCargoManagement.classes';

type RowData = {
    id: number;
    shippingCompanyName: string;
    cityName: string;
    countyName: string;
    neighborhoodName: string;
    isActiveForTomorrowDelivery: boolean;
    isActiveForExpressDelivery: boolean;
    isActiveForTodayDelivery: boolean;
    [key: string]: string | number | boolean;
};

interface DeliveryListTableProps {
    rows: RowData[];
    onEdit?: (row: RowData) => void;
    columns: ExtendedGridColDef[];
}

const DeliveryListTable: React.FC<DeliveryListTableProps> = ({ rows, onEdit, columns }) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const switchClassForField = (field: string) => {
        if (field === 'isActiveForTomorrowDelivery') return FDCM.inputSwitchTomorrowDeliveryCell;
        if (field === 'isActiveForExpressDelivery') return FDCM.inputSwitchExpressDeliveryCell;
        if (field === 'isActiveForTodayDelivery') return FDCM.inputSwitchTodayDeliveryCell;
        return undefined;
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Paper className={FDCM.deliveryTablePaper}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.field}>{column.headerName}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                            <TableRow key={row.id}>
                                {columns.map((column) => {
                                    if (column.field === 'shippingCompanyName') {
                                        return (
                                            <TableCell key={column.field}>
                                                <span
                                                    style={{
                                                        background:
                                                            row.shippingCompanyName === 'Hepsijet'
                                                                ? '#e3f0ff'
                                                                : '#e6f4ea',
                                                        color:
                                                            row.shippingCompanyName === 'Hepsijet'
                                                                ? '#1976d2'
                                                                : '#388e3c',
                                                        borderRadius: 8,
                                                        padding: '2px 12px',
                                                        fontWeight: 600,
                                                        fontSize: 14,
                                                        display: 'inline-block',
                                                    }}
                                                >
                                                    {row.shippingCompanyName}
                                                </span>
                                            </TableCell>
                                        );
                                    }
                                    if (
                                        column.field === 'isActiveForTomorrowDelivery' ||
                                        column.field === 'isActiveForExpressDelivery' ||
                                        column.field === 'isActiveForTodayDelivery'
                                    ) {
                                        return (
                                            <TableCell key={column.field}>
                                                <Switch
                                                    className={switchClassForField(column.field)}
                                                    checked={row[column.field] as boolean}
                                                    disabled
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#00B8F4',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#00B8F4',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                        );
                                    }
                                    if (column.field === 'actions') {
                                        return (
                                            <TableCell
                                                key={column.field}
                                                sx={{ p: 0, height: 1, minWidth: 56, minHeight: 56 }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '56px',
                                                        minHeight: '56px',
                                                    }}
                                                >
                                                    <Button
                                                        className={FDCM.inputButtonEditRow}
                                                        onClick={() => onEdit && onEdit(row)}
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            backgroundColor: '#00B8F4',
                                                            borderRadius: 1.5,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: 'none',
                                                            p: 0,
                                                            m: 0,
                                                            '&:hover': {
                                                                backgroundColor: '#0099c8',
                                                                boxShadow: 'none',
                                                            },
                                                        }}
                                                    >
                                                        <EditIcon sx={{ color: 'white', fontSize: 14 }} />
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        );
                                    }
                                    return <TableCell key={column.field}>{row[column.field]}</TableCell>;
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                className={FDCM.tablePagination}
                rowsPerPageOptions={[5, 10, 25, 100, 200]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('general.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Paper>
    );
};

export default DeliveryListTable;
